import React, { useCallback, useEffect, useRef, useState } from 'react';
import DesignEditor from './DesignEditor';
import './DesignEditor.css';

/**
 * DesignEditorModal — wraps the DesignEditor in either a centered modal
 * (default, unchanged — the calendar EventForm + legacy KFileDrop depend on this
 * pixel-for-pixel) or a full-viewport takeover shell (`fullScreen`).
 *
 * Props (external contract — STABLE):
 *   open                    – boolean, controls visibility
 *   onClose                 – called when the user closes without exporting
 *   onExport(base64, json)  – called with the final data-URL + fabric JSON
 *   title                   – header title (default: "Editor imagine")
 *   initialImage            – optional base64/URL to pre-load
 *   designJson              – optional saved fabric.js JSON to restore a design
 *   eventImages             – array of { name, src } for the "Eveniment" toolbar
 *   preset                  – optional { w, h } canvas size
 *   sizeToImage             – size the canvas to the loaded image (product edit)
 *   fullScreen              – NEW, optional. false/undefined = centered modal;
 *                             true = full-viewport takeover (title bar with
 *                             Salveaza / Renunta, editor fills the screen).
 *
 * Escape handling is TWO-STAGE and owned by the editor: it first exits IText
 * editing / an active mode (crop, mask, ...), and only bubbles to this modal's
 * dirty-guarded close via `onRequestClose`. The modal therefore does NOT attach
 * its own Escape listener (that would skip stage 1).
 */
const DesignEditorModal = ({
  open, onClose, onExport, title, initialImage, designJson, eventImages, preset, sizeToImage, fullScreen,
}) => {
  const [dirty, setDirty] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => { if (open) setDirty(false); }, [open]);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Ai modificari nesalvate. Inchizi editorul fara sa salvezi?')) return;
    onClose();
  }, [dirty, onClose]);

  const handleExport = useCallback((dataUrl, json) => {
    onExport(dataUrl, json);
    onClose();
  }, [onExport, onClose]);

  const save = useCallback(() => {
    if (editorRef.current) editorRef.current.handleExport();
  }, []);

  if (!open) return null;

  const editor = (
    <DesignEditor
      ref={editorRef}
      fullScreen={fullScreen}
      initialImage={initialImage}
      designJson={designJson}
      eventImages={eventImages}
      preset={preset}
      sizeToImage={sizeToImage}
      onDirtyChange={setDirty}
      onRequestClose={requestClose}
      onExport={handleExport}
    />
  );

  if (fullScreen) {
    return (
      <div className="de-fullscreen-overlay">
        <div className="de-fullscreen">
          <div className="de-fullscreen-header">
            <h3>{title || 'Editor imagine'}</h3>
            <div className="de-fullscreen-actions">
              <button className="de-btn" onClick={requestClose}>Renunta</button>
              <button className="de-btn de-btn-primary" onClick={save}>Salveaza</button>
            </div>
          </div>
          <div className="de-fullscreen-body">
            {editor}
          </div>
        </div>
      </div>
    );
  }

  return (
    // No backdrop-click-to-close: a stray click outside the panel must not
    // silently discard an in-progress edit. Close via the button or Escape.
    <div className="de-modal-overlay">
      <div className="de-modal">
        <div className="de-modal-header">
          <h3>{title || 'Editor imagine'}</h3>
          <button className="de-modal-close" onClick={requestClose}>&#10005;</button>
        </div>
        {editor}
      </div>
    </div>
  );
};

export default DesignEditorModal;
